import {
  createBookSearch,
  createReader,
  defaultReadingPreferences,
  fixedLayoutPreferences,
  effectiveFeatures,
  withBookDecisions,
  isChatAvailable,
  openPublication,
  ReaderError,
  autoColumnsFor,
  bookLanguage,
  browserSpeechPlayer,
  chapterLines,
  isReflowable,
  readAloud,
  resolveHref,
  resolveQuoteLocator,
  type SpeechPlayer,
  readingThemes,
  resolveTheme,
  sectionLabeler,
  sectionTitle,
  tableOfContents,
  totalProgression,
  type LocatorJson,
  type ReaderController,
  type ReaderErrorCode,
  type ReadingPreferences,
  type BookSearch,
  type ScreenRect,
  type SearchHit,
  type TextSelectionInfo,
  type TocEntry,
} from "@msb/reader-bridge";
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import { addBookmark, findBookmark, removeBookmark } from "./bookmarks.ts";
import { bookDetails, bookOf, plainDescription, type BookInfo } from "./bookSheet.ts";
import { BookSheet } from "./components/BookSheet.tsx";
import { emptyHistory, positionOf, recordJump, stepBackward, stepForward, type NavigationHistory } from "./history.ts";
import { defineMessages, useLocale, useT } from "./i18n/index.ts";
import { Icon } from "./icons/Icon.tsx";
import { AnnotationCard } from "./components/AnnotationCard.tsx";
import { NotesPanel } from "./components/NotesPanel.tsx";
import { SelectionToolbar } from "./components/SelectionToolbar.tsx";
import { DefinitionSheet } from "./components/DefinitionSheet.tsx";
import { isForeign, sourceLanguage, targetLanguage, translateOnServer } from "./translate.ts";
import { cleanSelection, explainQuestion } from "./explain.ts";
import { getLocale } from "./i18n/index.ts";
import { chapterRank } from "./recap.ts";
import {
  createAnnotation,
  loadAnnotations,
  removeAnnotation,
  saveAnnotations,
  sortAnnotations,
  tintOf,
  updateAnnotation,
  type Annotation,
  type HighlightColor,
} from "./annotations.ts";
import { ChatApi } from "./chat/api.ts";
import { useBookFeatures } from "./chat/bookFeatures.ts";
import { ChatPanel, type ChatConfig } from "./components/ChatPanel.tsx";
import { ContentLanguageProvider } from "./components/ContentLanguage.tsx";
import { serverTranslator } from "./contentLanguage.ts";
import { availableIn, ContentsPanel } from "./components/ContentsPanel.tsx";
import { ExcerptEnd } from "./components/ExcerptEnd.tsx";
import { ElsewhereDialog } from "./components/ElsewhereDialog.tsx";
import { Popover } from "./components/Popover.tsx";
import { PreferencesPanel, PreferencesMenu } from "./components/PreferencesPanel.tsx";
import { ReaderFooter } from "./components/ReaderFooter.tsx";
import { SearchPanel } from "./components/SearchPanel.tsx";
import { SearchTrail } from "./components/SearchTrail.tsx";
import { MobileChrome, ReaderToolbar, type MenuPanel, type ReadAloudControl } from "./components/ReaderToolbar.tsx";
import { config } from "./config.ts";
import { EMBED_WIDE_QUERY, useMediaQuery, WIDE_QUERY } from "./hooks/useMediaQuery.ts";
import type { OpenParams } from "./params.ts";
import { annotationsToOpen, bookmarksToOpen, positionToOpen, preferencesToOpen, recordAnnotations, recordBookmarks, recordLocation, recordPreferences } from "./bookSync.ts";
import { readingProgress } from "./progress.ts";
import {
  clampShare,
  loadBookmarks,
  loadLayout,
  loadLocation,
  loadLocationTime,
  loadPreferences,
  saveBookmarks,
  saveLayout,
  saveLocation,
  saveProgress,
  savePreferences,
  type Bookmark,
  type LayoutSettings,
} from "./storage.ts";
import { ReadingSession, syncFor } from "./sync.ts";

type Status = { kind: "loading" } | { kind: "ready" } | { kind: "error"; code: ReaderErrorCode | "unknown" };

const messages = defineMessages(
  {
    preferences: "Réglages",
    book: "Fiche du livre",
    contents: "Table des matières",
    bookmarks: "Signets",
    search: "Rechercher",
    notes: "Notes et signets",
    bookStart: "Début du livre",
    page: "Page {page}",
    notFound: "Ce passage n'a pas été retrouvé dans la page.",
    "manifest-unavailable": "Ce livre est momentanément indisponible. Réessayez dans un instant.",
    "manifest-invalid": "Ce livre n'a pas pu être lu : son fichier est invalide.",
    forbidden: "Accès refusé à cette publication.",
    expired: "Le lien de lecture a expiré.",
    "render-failed": "Impossible d'afficher ce livre.",
    unknown: "Impossible d'ouvrir ce livre.",
    reading: "Lecture",
    opening: "Ouverture du livre…",
    readAloudFailed: "La lecture à voix haute n’est pas possible : ce navigateur n’a pas de voix pour la langue du livre.",
    loadingPage: "Chargement de la page",
    previousPage: "Page précédente",
    nextPage: "Page suivante",
    showMenu: "Afficher le menu",
    menu: "Menu",
    readerWidth: "Largeur du volet de lecture",
  },
  {
    preferences: "Settings",
    book: "About this book",
    contents: "Contents",
    bookmarks: "Bookmarks",
    search: "Search",
    notes: "Notes and bookmarks",
    bookStart: "Start of the book",
    page: "Page {page}",
    notFound: "This passage could not be found on the page.",
    "manifest-unavailable": "This book is temporarily unavailable. Please try again in a moment.",
    "manifest-invalid": "This book could not be read: its file is invalid.",
    forbidden: "You don't have access to this book.",
    expired: "This reading link has expired.",
    "render-failed": "This book could not be displayed.",
    unknown: "This book could not be opened.",
    reading: "Reading",
    opening: "Opening the book…",
    readAloudFailed: "Reading aloud isn't possible: this browser has no voice for the language of the book.",
    loadingPage: "Loading page",
    previousPage: "Previous page",
    nextPage: "Next page",
    showMenu: "Show menu",
    menu: "Menu",
    readerWidth: "Reading pane width",
  },
);

const darkQuery = window.matchMedia("(prefers-color-scheme: dark)");

const initialLayout = (content: HTMLElement, constraint: number) => {
  const width = content.parentElement?.clientWidth ?? 0;
  return { constraint, autoColumns: width > 0 ? autoColumnsFor(width - constraint) : (1 as const) };
};

/** Largeur réservée de chaque côté du texte pour les flèches de page. */
const TURN_MARGIN = { wide: 64, compact: 16 };
/** Mobile : un tap dans les 10 % de gauche ou de droite tourne la page, ailleurs il ouvre l'interface. */
const EDGE_TAP_RATIO = 0.1;

const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));

export function App({ params }: { params: OpenParams }) {
  const t = useT(messages);
  const locale = useLocale();
  const isFull = params.accessMode === "full";
  // Extrait dans la fiche produit : le site affiche le titre et l'achat, le reader le Sommaire et les Réglages.
  const embedded = !!params.embedded;
  const wide = useMediaQuery(embedded ? EMBED_WIDE_QUERY : WIDE_QUERY);
  const container = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const pane = useRef<HTMLElement>(null);
  const reader = useRef<ReaderController>();
  const [status, setStatus] = useState<Status>({ kind: "loading" });
  const [title, setTitle] = useState("");
  const [bookInfo, setBookInfo] = useState<BookInfo>();
  const [bookId, setBookId] = useState<string>();
  const [toc, setToc] = useState<TocEntry[]>([]);
  // Fichiers de l'ordre de lecture et nom de leur section : le rang du chapitre en cours, pour le récap.
  const [chapters, setChapters] = useState<{ hrefs: string[]; titles: (string | undefined)[] }>({ hrefs: [], titles: [] });
  const [positionCount, setPositionCount] = useState(0);
  // Read from right to left (OPF page-progression-direction, else the language): arrows, edges and slider are mirrored.
  const [rtl, setRtl] = useState(false);
  const [locator, setLocator] = useState<LocatorJson>();
  const [isDark, setIsDark] = useState(darkQuery.matches);
  const [prefs, setPrefs] = useState<ReadingPreferences>(() => loadPreferences());
  // Livre à mise en page fixe : ses propres couleurs (Auto, Clair, Sombre) et le choix d'une ou deux pages.
  const [fixedLayout, setFixedLayout] = useState(false);
  const readerPrefs = useMemo(() => (fixedLayout ? fixedLayoutPreferences(prefs) : prefs), [prefs, fixedLayout]);
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  // Synchro avec le compte (position, signets, réglages de lecture) : livres complets seulement, avec un compte connu.
  const sync = useMemo(() => (isFull && params.productId ? syncFor(params.chatToken) : undefined), []);
  const session = useRef<ReadingSession>();
  const lastProgress = useRef<number>();
  const changePrefs = (next: ReadingPreferences) => {
    setPrefs(next);
    recordPreferences(sync, next);
  };
  const [layout, setLayout] = useState<LayoutSettings>(() => (params.openChat ? { ...loadLayout(), chatOpen: true } : loadLayout()));
  // Lecture à voix haute avec la voix de l'appareil, comme dans l'app : de la page à l'écran, la page suit la voix.
  const [speech] = useState<SpeechPlayer | undefined>(() => browserSpeechPlayer());
  const [readingAloud, setReadingAloud] = useState(false);
  const [speechFailed, setSpeechFailed] = useState(false);
  // La position d'un autre appareil, plus récente et ailleurs, proposée à l'ouverture du livre.
  const [elsewhere, setElsewhere] = useState<LocatorJson>();
  // Mobile : interface masquée par défaut, affichée par un tap au centre, comme dans l'app.
  const [chromeVisible, setChromeVisible] = useState(false);
  // Sur mobile, le navigateur envoie un clic « fantôme » juste après le tap qui a ouvert
  // l'interface : il tomberait sur le voile et la refermerait aussitôt.
  const chromeOpenedAt = useRef(0);
  const showChrome = () => {
    chromeOpenedAt.current = performance.now();
    setChromeVisible(true);
  };
  const hideChromeFromScrim = () => {
    if (performance.now() - chromeOpenedAt.current > 450) {
      setChromeVisible(false);
      enterMobileFullscreen();
    }
  };
  /*
   * Mobile : la lecture passe en plein écran au premier geste (tour de page, fermeture du menu),
   * ce qui masque la barre d'adresse et la barre de navigation du navigateur. Le geste « retour »
   * d'Android en sort. Sans effet là où le navigateur ne le permet pas (Safari sur iPhone).
   */
  const enterMobileFullscreen = () => {
    if (wideRef.current || document.fullscreenElement || !document.fullscreenEnabled) return;
    void document.documentElement.requestFullscreen?.({ navigationUI: "hide" }).catch(() => {});
  };
  // The page on the left or on the right: in a book read from right to left, the next page is on the left.
  const turnPage = (side: "left" | "right") => {
    enterMobileFullscreen();
    void (side === "left" ? reader.current?.goLeft() : reader.current?.goRight());
  };
  const [sheetChatOpen, setSheetChatOpen] = useState(!!params.openChat);
  const [panel, setPanel] = useState<{ name: MenuPanel; anchor: HTMLElement | null } | null>(null);
  const [dragging, setDragging] = useState(false);
  const [history, setHistory] = useState<NavigationHistory>(emptyHistory);
  // Plein écran (grand écran) : seulement le texte, les flèches et le bouton menu.
  const [immersive, setImmersive] = useState(false);
  // Largeur du volet de lecture : colonnes en mode Auto.
  const [paneWidth, setPaneWidth] = useState(0);
  // Pendant que Readium recompose le texte (plein écran, chat ouvert ou fermé), on le masque puis
  // on le fait réapparaître en fondu, au lieu de montrer les étapes intermédiaires.
  const [settling, setSettling] = useState(false);
  // Navigation en cours (changement de fichier HTML parfois lent) : indicateur de chargement.
  const [busy, setBusy] = useState(false);
  const buttonRefs = {
    preferences: useRef<HTMLButtonElement>(null),
    contents: useRef<HTMLButtonElement>(null),
    bookmarks: useRef<HTMLButtonElement>(null),
    search: useRef<HTMLButtonElement>(null),
    book: useRef<HTMLButtonElement>(null),
  };
  // Recherche dans le texte du livre : les fichiers sont lus une fois, la dernière requête est gardée.
  const [bookSearch, setBookSearch] = useState<BookSearch>();
  const lastQuery = useRef("");
  const tRef = useRef(t);
  tRef.current = t;
  // Les occurrences en cours de lecture : une barre passe de l'une à l'autre une fois le panneau fermé.
  const [trail, setTrail] = useState<{ hits: SearchHit[]; index: number; searching?: boolean } | null>(null);
  // Occurrence choisie avant la fin de la recherche : le livre continue d'être parcouru pour la barre.
  const trailSearch = useRef<AbortController | null>(null);
  const [notFound, setNotFound] = useState(false);
  // Annotations (surlignages avec une note) : gardées dans ce navigateur, dessinées sur les pages par le reader.
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [selection, setSelection] = useState<TextSelectionInfo | null>(null);
  const selectionRef = useRef<TextSelectionInfo | null>(null);
  const [editing, setEditing] = useState<{ id: string; rect?: ScreenRect } | null>(null);

  const book = { isSmartbook: params.isSmartbook };
  // Le chat demande aussi un jeton et le produit : sans eux, on ne propose pas le bouton.
  const chatConfig: ChatConfig | undefined =
    isChatAvailable(params.features, book, params.accessMode) && params.chatToken && params.productId
      ? { baseUrl: config.aiApiUrl, token: params.chatToken, productId: params.productId }
      : undefined;
  // Décisions du back-office pour ce livre, lues une fois à l'ouverture (comme Studio) : récap, explication…
  const chatApi = useMemo(
    () => (chatConfig ? new ChatApi({ baseUrl: chatConfig.baseUrl, token: chatConfig.token }) : undefined),
    [chatConfig?.baseUrl, chatConfig?.token],
  );
  const bookFeatures = useBookFeatures(chatApi, chatConfig?.productId);
  // Les drapeaux de la langue du chat (livre / interface) : réponses et quiz traduits par le serveur IA.
  const translateMany = useMemo(
    () =>
      chatConfig
        ? serverTranslator(`${chatConfig.baseUrl.replace(/\/+$/, "")}/server/translate/many`, {
            Authorization: `Bearer ${chatConfig.token}`,
            "X-Auth-Provider": "reader-application",
          })
        : undefined,
    [chatConfig?.baseUrl, chatConfig?.token],
  );
  const features = useMemo(
    () => withBookDecisions(effectiveFeatures(params.features, book, params.accessMode), bookFeatures?.features),
    [params.features, params.isSmartbook, params.accessMode, bookFeatures],
  );
  const chatAvailable = chatConfig !== undefined;
  const bookmarksEnabled = isFull;
  const annotationsEnabled = features.highlights || features.notes;
  const searchEnabled = features.search && !!bookSearch;

  // Sur grand écran le chat est ouvert à l'ouverture du livre (sauf si le lecteur l'a masqué) ;
  // sur mobile il s'ouvre à la demande, en feuille du bas.
  const chatOpen = chatAvailable && (wide ? layout.chatOpen : sheetChatOpen);
  const split = wide && chatOpen && !immersive;
  // Grand écran : marges réservées aux flèches de page, de chaque côté du texte.
  const constraint = 2 * (wide ? TURN_MARGIN.wide : TURN_MARGIN.compact);
  const autoColumns = paneWidth > 0 ? autoColumnsFor(paneWidth - constraint) : 1;
  const pageLayout = useMemo(() => ({ constraint, autoColumns }), [constraint, autoColumns]);
  const pageLayoutRef = useRef(pageLayout);
  const wideRef = useRef(wide);
  wideRef.current = wide;
  useEffect(() => reader.current?.setEdgeTapRatio(wide ? 0 : EDGE_TAP_RATIO), [wide]);
  pageLayoutRef.current = pageLayout;

  useEffect(() => {
    const el = pane.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setPaneWidth(Math.round(entry?.contentRect.width ?? 0)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const firstLayout = useRef(true);
  useEffect(() => {
    if (firstLayout.current) {
      firstLayout.current = false;
      return;
    }
    setSettling(true);
    const timer = setTimeout(() => setSettling(false), 450);
    return () => clearTimeout(timer);
  }, [split, immersive]);
  // Le chat reste monté une fois ouvert : la conversation et la saisie survivent aux fermetures.
  const [chatMounted, setChatMounted] = useState(false);
  useEffect(() => {
    if (chatOpen) setChatMounted(true);
  }, [chatOpen]);

  useEffect(() => {
    const onChange = (e: MediaQueryListEvent) => setIsDark(e.matches);
    darkQuery.addEventListener("change", onChange);
    return () => darkQuery.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const publication = await openPublication(params.manifestUrl);
        if (cancelled || !container.current) return;
        // La position et les réglages des autres appareils, si le serveur répond vite ; sinon on ouvre sans attendre.
        const openPrefs = await (async () => {
          if (!sync) return prefs;
          await sync.syncWithin(1500);
          const account = preferencesToOpen(sync);
          return account ? { ...prefs, ...account } : prefs;
        })();
        if (cancelled || !container.current) return;
        if (openPrefs !== prefs) setPrefs(openPrefs);
        setTitle(publication.metadata.title.getTranslation() ?? "");
        const fixed = !isReflowable(publication);
        setFixedLayout(fixed);
        setBookInfo(bookOf(publication, params.manifestUrl));
        setToc(tableOfContents(publication));
        const id = publication.metadata.identifier ?? params.manifestUrl;
        setBookId(id);
        if (isFull) setBookmarks(bookmarksToOpen(sync, params.productId, loadBookmarks(id)));
        if (isFull) setAnnotations(annotationsToOpen(sync, params.productId, loadAnnotations(id)));
        if (sync && params.productId) {
          sync.event("book_opened", params.productId);
          session.current = new ReadingSession(sync, params.productId);
        }
        const opening = isFull
          ? positionToOpen(sync, params.productId, loadLocation(id), loadLocationTime(id))
          : { open: undefined, elsewhere: undefined };
        setElsewhere(opening.elsewhere);
        reader.current = await createReader({
          container: container.current,
          publication,
          preferences: fixed ? fixedLayoutPreferences(openPrefs) : openPrefs,
          isSystemDark: isDark,
          // Mise en page calculée sur la largeur réelle du volet dès l'ouverture : pas de
          // recomposition juste après, qui ferait perdre la page mémorisée.
          layout: initialLayout(container.current, pageLayoutRef.current.constraint),
          // Le balayage est réservé au geste « retour » d'Android : on tourne les pages par un tap.
          disableSwipe: true,
          edgeTapRatio: wideRef.current ? 0 : EDGE_TAP_RATIO,
          initialLocator: opening.open,
          callbacks: {
            onPositionChanged: (l) => {
              setLocator(l);
              lastProgress.current = readingProgress(l, reader.current?.positionCount);
              // La sélection disparaît quand la page tourne, pas à chaque message de progression de la même page.
              const picked = selectionRef.current;
              if (picked && (picked.locator.href !== l.href || picked.locator.locations?.position !== l.locations?.position)) {
                selectionRef.current = null;
                setSelection(null);
              }
              if (isFull) {
                saveLocation(id, l);
                recordLocation(sync, params.productId, l);
              }
              if (isFull && params.libraryBookId) {
                const progress = readingProgress(l, reader.current?.positionCount);
                if (progress !== undefined) saveProgress(params.libraryBookId, progress);
              }
            },
            onCenterTapped: () => {
              selectionRef.current = null;
              setSelection(null);
              showChrome();
            },
            onSelection: (info) => {
              selectionRef.current = info;
              setSelection(info);
            },
            onAnnotationActivated: (id, rect) => {
              selectionRef.current = null;
              setSelection(null);
              setEditing({ id, rect });
            },
            onExternalLink: (href) => window.open(href, "_blank", "noopener"),
            onInternalLink: () => rememberCurrent(),
            onBusyChanged: setBusy,
          },
        });
        if (cancelled) return;
        const controller = reader.current;
        const labelOf = sectionLabeler(publication, tableOfContents(publication));
        const files = publication.readingOrder.items;
        setChapters({ hrefs: files.map((file) => file.href), titles: files.map((file, i) => labelOf(file.href, i)) });
        setBookSearch(
          createBookSearch(publication, {
            // Les noms suivent la langue de l'interface, même quand elle change après l'ouverture.
            labelOf: (href, index) =>
              fixed ? tRef.current("page", { page: index + 1 }) : labelOf(href, index) ?? tRef.current("bookStart"),
            positionOf: (href, progression) => controller.positionOf(href, progression),
          }),
        );
        setPositionCount(reader.current.positionCount);
        setRtl(reader.current.rtl);
        setStatus({ kind: "ready" });
      } catch (e) {
        if (cancelled) return;
        setStatus({ kind: "error", code: e instanceof ReaderError ? e.code : "unknown" });
      }
    })();
    return () => {
      cancelled = true;
      setReadingAloud(false);
      void reader.current?.destroy();
      reader.current = undefined;
    };
  }, [params.manifestUrl]);

  useEffect(() => {
    const r = reader.current;
    if (!readingAloud || !speech || !r) return;
    const abort = new AbortController();
    setSpeechFailed(false);
    const lines = new Map<string, string[]>();
    const loadLines = async (href: string) => {
      const known = lines.get(href);
      if (known) return known;
      const link = r.publication.readingOrder.items.find((item) => item.href === href);
      const found = link ? chapterLines((await r.publication.get(link).readAsString()) ?? "") : [];
      lines.set(href, found);
      return found;
    };
    void readAloud(r, loadLines, speech, bookLanguage(r.publication), abort.signal).then((completed) => {
      if (abort.signal.aborted) return;
      // La voix n'a pas pu parler (pas de voix pour la langue du livre sur cet appareil) : on le dit un moment.
      if (!completed) setSpeechFailed(true);
      setReadingAloud(false);
    });
    return () => abort.abort();
  }, [readingAloud, speech]);
  useEffect(() => {
    if (!speechFailed) return;
    const timer = setTimeout(() => setSpeechFailed(false), 6000);
    return () => clearTimeout(timer);
  }, [speechFailed]);

  useEffect(() => {
    void reader.current?.submitPreferences(readerPrefs, isDark, pageLayout);
    if (isFull) savePreferences(prefs);
  }, [readerPrefs, isDark, pageLayout]);

  useEffect(() => {
    saveLayout(layout);
  }, [layout]);

  // Temps de lecture (onglet visible) et envoi des modifications quand l'onglet se cache ou se ferme.
  useEffect(() => {
    if (!sync) return;
    const onVisibility = () => {
      session.current?.visibilityChanged();
      if (document.visibilityState === "hidden") void sync.flush();
    };
    const end = () => {
      session.current?.end(lastProgress.current);
      void sync.flush();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", end);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", end);
      end();
    };
  }, [sync]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e.target)) return;
      if (e.key === "ArrowRight") void reader.current?.goRight();
      if (e.key === "ArrowLeft") void reader.current?.goLeft();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const closePanel = useCallback(() => setPanel(null), []);

  // Historique : chaque saut (table des matières, signet, curseur, lien interne) mémorise la page quittée.
  const rememberCurrent = () => {
    const current = reader.current?.currentLocator;
    if (current) setHistory((h) => recordJump(h, current));
  };
  const jump = (go: (r: ReaderController) => Promise<boolean>) => {
    const r = reader.current;
    if (!r) return;
    rememberCurrent();
    void go(r);
  };
  // Annotations : dessinées sur les pages, enregistrées à chaque changement. Rien de cela ne déplace le lecteur.
  useEffect(() => {
    if (status.kind !== "ready") return;
    reader.current?.setAnnotations(annotations.map((a) => ({ id: a.id, locator: a.locator, tint: tintOf(a.color) })));
  }, [annotations, status.kind]);
  const storeAnnotations = (next: Annotation[]) => {
    setAnnotations(next);
    if (bookId) saveAnnotations(bookId, next);
    recordAnnotations(sync, params.productId, annotations, next);
  };
  const addHighlight = (color: HighlightColor, thenNote = false) => {
    if (!selection) return;
    const annotation = createAnnotation({ ...selection.locator, title: sectionTitle(toc, selection.locator.href) }, color);
    storeAnnotations(sortAnnotations([...annotations, annotation]));
    reader.current?.clearSelection();
    selectionRef.current = null;
    setSelection(null);
    if (thenNote) setEditing({ id: annotation.id, rect: selection.rect });
  };
  const changeAnnotation = (id: string, changes: { color?: HighlightColor; note?: string }) =>
    storeAnnotations(updateAnnotation(annotations, id, changes));
  const deleteAnnotation = (id: string) => {
    storeAnnotations(removeAnnotation(annotations, id));
    setEditing((current) => (current?.id === id ? null : current));
  };
  const editedAnnotation = editing ? annotations.find((a) => a.id === editing.id) : undefined;

  // « Explique-moi » : un mot ou deux sont définis ; un passage plus long peut être expliqué dans le chat.
  const [definition, setDefinition] = useState<{ term: string; rect?: ScreenRect; translation?: { source: string; target: string } } | null>(null);
  const [ask, setAsk] = useState<{ text: string; nonce: number }>();
  const explainPassage = (passage: string, rect?: ScreenRect) => {
    const text = cleanSelection(passage);
    if (text) setDefinition({ term: text, rect });
  };
  const explainSelection = () => {
    if (!selection) return;
    reader.current?.clearSelection();
    selectionRef.current = null;
    setSelection(null);
    explainPassage(selection.locator.text?.highlight ?? "", selection.rect);
  };
  // « Traduire » : par le serveur, dans la fenêtre d'« Explique-moi », vers la langue de l'appareil.
  const passageLanguage = (text: string) => sourceLanguage(text, reader.current?.publication.metadata.languages?.[0]);
  const translationOf = (text: string) => {
    const source = passageLanguage(text);
    return { source, target: targetLanguage(source, navigator.languages ?? [navigator.language], getLocale()) };
  };
  const translateOnAiServer = chatConfig
    ? (text: string, source: string, target: string, signal: AbortSignal) =>
        translateOnServer(`${chatConfig.baseUrl.replace(/\/+$/, "")}/server/translate`, { Authorization: `Bearer ${chatConfig.token}`, "X-Auth-Provider": "reader-application" }, text, source, target, signal)
    : undefined;
  const translateSelection = () => {
    if (!selection) return;
    reader.current?.clearSelection();
    selectionRef.current = null;
    setSelection(null);
    const text = cleanSelection(selection.locator.text?.highlight ?? "");
    if (text) setDefinition({ term: text, rect: selection.rect, translation: translationOf(text) });
  };
  // Dans la barre seulement pour un passage dans une autre langue que celle de l'appareil et de l'interface ; sinon avec
  // Wikipédia et Google dans la fenêtre d'« Explique-moi » (dans la barre aussi quand Explique-moi n'est pas proposé).
  const translateInBar =
    !features.explain ||
    (selection !== null && isForeign(passageLanguage(selection.locator.text?.highlight ?? ""), navigator.language, getLocale()));
  const askChat = (text: string) => {
    setDefinition(null);
    setAsk((current) => ({ text: explainQuestion(text, getLocale()), nonce: (current?.nonce ?? 0) + 1 }));
    if (wideRef.current) setLayout((l) => ({ ...l, chatOpen: true }));
    else setSheetChatOpen(true);
  };

  /** Un passage cité par le chat : ouvert dans le livre et surligné ; sur téléphone, le chat s'efface devant lui. */
  const openQuote = (quote: LocatorJson) => {
    const r = reader.current;
    if (!r) return;
    const locator = resolveQuoteLocator(r.publication.readingOrder.items, quote);
    closeTrail();
    if (!locator) {
      setNotFound(true);
      return;
    }
    if (!wideRef.current) setSheetChatOpen(false);
    rememberCurrent();
    r.highlight(null);
    void r.goTo(locator).then((ok) => {
      setNotFound(!ok);
      if (ok) r.highlight(locator);
    });
  };

  /** Ouvre l'occurrence `index` d'une recherche et la surligne. */
  const showOccurrence = (hits: SearchHit[], index: number) => {
    const r = reader.current;
    const hit = hits[index];
    if (!r || !hit) return;
    setTrail((current) => ({ hits, index, searching: current?.searching }));
    setNotFound(false);
    r.highlight(null);
    void r.goTo(hit.locator).then((ok) => {
      setNotFound(!ok);
      if (ok) r.highlight(hit.locator);
    });
  };
  const searchOnForTrail = (query: string) => {
    trailSearch.current?.abort();
    if (!bookSearch) return;
    const controller = new AbortController();
    trailSearch.current = controller;
    const found: SearchHit[] = [];
    setTrail((current) => current && { ...current, searching: true });
    const done = () => {
      if (controller.signal.aborted) return;
      setTrail((current) => current && { ...current, hits: found.length >= current.hits.length ? [...found] : current.hits, searching: false });
    };
    bookSearch
      .run(query, {
        signal: controller.signal,
        onHits: (hits) => {
          found.push(...hits);
          if (!controller.signal.aborted) setTrail((current) => (current && found.length > current.hits.length ? { ...current, hits: [...found] } : current));
        },
        onProgress: () => {},
      })
      .then(done, done);
  };
  const closeTrail = () => {
    trailSearch.current?.abort();
    setTrail(null);
    setNotFound(false);
    reader.current?.highlight(null);
  };
  useEffect(() => {
    if (!notFound) return;
    const timer = setTimeout(() => setNotFound(false), 4000);
    return () => clearTimeout(timer);
  }, [notFound]);
  useEffect(() => () => trailSearch.current?.abort(), []);

  const historyStep = (step: typeof stepBackward) => {
    const r = reader.current;
    if (!r) return;
    const result = step(history, r.currentLocator);
    if (!result) return;
    setHistory(result.history);
    void r.goTo(result.target);
  };

  const enterImmersive = () => {
    setPanel(null);
    setSettling(true);
    setImmersive(true);
    // Vrai plein écran du navigateur quand c'est permis ; sinon, l'interface est seulement masquée.
    void document.documentElement.requestFullscreen?.().catch(() => {});
  };
  const leaveImmersive = async () => {
    // Quitter le plein écran du navigateur d'abord, puis réafficher l'interface : une seule
    // recomposition visible, masquée par le fondu.
    setSettling(true);
    if (document.fullscreenElement) await document.exitFullscreen().catch(() => {});
    setImmersive(false);
  };
  useEffect(() => {
    // Échap (ou le navigateur) quitte le plein écran : on réaffiche l'interface.
    const onChange = () => {
      if (!document.fullscreenElement) setImmersive(false);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  useEffect(() => {
    if (!wide) setImmersive(false);
  }, [wide]);
  const openPanel = (name: MenuPanel, anchor: HTMLElement | null) =>
    setPanel((current) => (current?.name === name ? null : { name, anchor }));

  const toggleChat = () => {
    if (wide) setLayout((l) => ({ ...l, chatOpen: !l.chatOpen }));
    else setSheetChatOpen((v) => !v);
  };
  const closeChat = () => {
    if (wide) setLayout((l) => ({ ...l, chatOpen: false }));
    else setSheetChatOpen(false);
  };

  const current = findBookmark(bookmarks, locator);
  const toggleBookmark = () => {
    if (!bookId || !locator) return;
    // Le signet garde le titre de la section, même quand Readium ne le fournit pas.
    const title = locator.title || sectionTitle(toc, locator.href);
    const next = current ? removeBookmark(bookmarks, current.id) : addBookmark(bookmarks, { ...locator, title });
    setBookmarks(next);
    saveBookmarks(bookId, next);
    recordBookmarks(sync, params.productId, bookmarks, next);
  };
  const deleteBookmark = (b: Bookmark) => {
    if (!bookId) return;
    const next = removeBookmark(bookmarks, b.id);
    setBookmarks(next);
    saveBookmarks(bookId, next);
    recordBookmarks(sync, params.productId, bookmarks, next);
  };

  // Séparateur entre la lecture et le chat : glisser, ou flèches du clavier.
  const onSplitterDown = (e: PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setDragging(true);
  };
  const onSplitterMove = (e: PointerEvent) => {
    if (!dragging || !body.current) return;
    const r = body.current.getBoundingClientRect();
    setLayout((l) => ({ ...l, readerShare: clampShare((e.clientX - r.left) / r.width) }));
  };
  const onSplitterUp = () => setDragging(false);
  const onSplitterKey = (e: KeyboardEvent) => {
    const delta = e.key === "ArrowLeft" ? -0.05 : e.key === "ArrowRight" ? 0.05 : 0;
    if (!delta) return;
    e.preventDefault();
    e.stopPropagation();
    setLayout((l) => ({ ...l, readerShare: clampShare(l.readerShare + delta) }));
  };

  const progression = locator ? totalProgression(locator) : undefined;
  const position = typeof locator?.locations?.position === "number" ? locator.locations.position : undefined;
  const excerptFinished = !isFull && progression !== undefined && progression >= 0.98;
  const section = locator?.title || sectionTitle(toc, locator?.href);
  const reading = useMemo(() => {
    const href = locator?.href.split("#")[0];
    const index = href ? chapters.hrefs.findIndex((file) => file.split("#")[0] === href) : -1;
    return {
      chapter: chapterRank(chapters.titles, index),
      section: section || chapters.titles[index],
      href: locator?.href,
      readingOrder: chapters.hrefs,
    };
  }, [chapters, locator?.href, section]);
  // Le lecteur est-il arrivé à ce fichier ? Les personnes qui apparaissent plus loin ne sont pas proposées (comme Studio).
  const reached = useCallback(
    (href: string) => {
      const files = chapters.hrefs.map((file) => ({ href: file }));
      const target = resolveHref(files, href);
      const current = locator?.href ? resolveHref(files, locator.href) : undefined;
      // Fichier inconnu du livre : proposée ; position pas encore connue : rien qui puisse dévoiler la suite.
      if (!target) return true;
      if (!current) return false;
      return chapters.hrefs.indexOf(target) <= chapters.hrefs.indexOf(current);
    },
    [chapters, locator?.href],
  );
  const showMobileChrome = !wide && chromeVisible && !sheetChatOpen;
  // Livres recomposables seulement, comme l'app : une page fixe est une image de l'imprimé.
  const readAloudControl: ReadAloudControl | undefined =
    features.tts && speech && status.kind === "ready" && reader.current && isReflowable(reader.current.publication)
      ? {
          reading: readingAloud,
          onToggle: () => {
            setReadingAloud((reading) => !reading);
            // L'interface s'efface devant le texte quand la voix commence.
            if (!readingAloud) setChromeVisible(false);
          },
        }
      : undefined;

  const footer = (
    <ReaderFooter
      position={position}
      positionCount={positionCount}
      progression={progression}
      rtl={rtl}
      backward={history.backward.length ? { position: positionOf(history.backward.at(-1)) } : undefined}
      forward={history.forward.length ? { position: positionOf(history.forward[0]) } : undefined}
      onSeek={(p) => jump((r) => r.goToPosition(p))}
      onHistoryBackward={() => historyStep(stepBackward)}
      onHistoryForward={() => historyStep(stepForward)}
    />
  );

  return (
    <div
      class="reader"
      data-layout={wide ? "wide" : "compact"}
      data-split={split ? "true" : "false"}
      data-dragging={dragging ? "true" : undefined}
      data-immersive={immersive ? "true" : undefined}
      data-embedded={embedded ? "true" : undefined}
      data-settling={settling ? "true" : undefined}
    >
      {wide && !immersive && (
        <ReaderToolbar
          embedded={embedded}
          title={title}
          section={section}
          excerpt={!isFull}
          backUrl={params.backUrl}
          bookmarked={!!current}
          bookmarksEnabled={bookmarksEnabled}
          searchEnabled={searchEnabled}
          notesEnabled={annotationsEnabled}
          chatAvailable={chatAvailable}
          chatOpen={chatOpen}
          openPanel={panel?.name ?? null}
          onToggleBookmark={toggleBookmark}
          onToggleChat={toggleChat}
          readAloud={readAloudControl}
          onFullscreen={enterImmersive}
          onPanel={openPanel}
          buttonRefs={buttonRefs}
        />
      )}

      <div class="reader-body" ref={body} style={{ "--reader-share": String(layout.readerShare) }}>
        <section
          ref={pane}
          class="reader-pane"
          aria-label={t("reading")}
          style={{
            "--reading-bg": readingThemes[resolveTheme(readerPrefs, isDark)].backgroundColor,
            "--reading-fg": readingThemes[resolveTheme(readerPrefs, isDark)].textColor,
          }}
        >
          <main class="reader-content" ref={container} aria-busy={status.kind === "loading"} />
          {status.kind === "loading" && <p class="reader-message">{t("opening")}</p>}
          {busy && status.kind === "ready" && <span class="reader-loader" role="progressbar" aria-label={t("loadingPage")} />}
          {status.kind === "error" && (
            <p class="reader-message" role="alert">
              {t(status.code)}
            </p>
          )}
          {notFound && (
            <p class="reader-note" role="status">
              {t("notFound")}
            </p>
          )}
          {trail && !panel && (
            <SearchTrail
              hits={trail.hits}
              index={trail.index}
              searching={trail.searching}
              onStep={(index) => showOccurrence(trail.hits, index)}
              onList={() => openPanel("search", wide ? buttonRefs.search.current : null)}
              onClose={closeTrail}
            />
          )}
          {speechFailed && (
            <p class="reader-note" role="status">
              {t("readAloudFailed")}
            </p>
          )}
          {(wide || !showMobileChrome) && (
            <>
              <button type="button" class="reader-turn reader-turn--prev" onClick={() => turnPage("left")} aria-label={rtl ? t("nextPage") : t("previousPage")}>
                <Icon name="chevronRight" size={wide ? 32 : 24} />
              </button>
              <button type="button" class="reader-turn reader-turn--next" onClick={() => turnPage("right")} aria-label={rtl ? t("previousPage") : t("nextPage")}>
                <Icon name="chevronRight" size={wide ? 32 : 24} />
              </button>
            </>
          )}
          {immersive && (
            <button type="button" class="msb-round-button reader-menu-button" onClick={() => void leaveImmersive()} aria-label={t("showMenu")} title={t("menu")}>
              <Icon name="menu" />
            </button>
          )}
          {!wide && !showMobileChrome && !embedded && (
            <button
              type="button"
              class="msb-round-button reader-menu-button"
              onClick={showChrome}
              aria-label={t("showMenu")}
              title={t("menu")}
            >
              <Icon name="menu" size={20} />
            </button>
          )}
          {wide && !immersive && footer}
        </section>

        {split && (
          <div
            class="reader-splitter"
            role="separator"
            aria-orientation="vertical"
            aria-label={t("readerWidth")}
            aria-valuemin={35}
            aria-valuemax={75}
            aria-valuenow={Math.round(layout.readerShare * 100)}
            tabIndex={0}
            onPointerDown={onSplitterDown}
            onPointerMove={onSplitterMove}
            onPointerUp={onSplitterUp}
            onPointerCancel={onSplitterUp}
            onKeyDown={onSplitterKey}
          />
        )}

        {chatConfig && chatMounted && (
          <aside class="reader-chat" data-open={chatOpen && !immersive ? "true" : "false"} aria-hidden={!chatOpen || immersive}>
            <ContentLanguageProvider bookKey={chatConfig.productId} bookLanguage={bookInfo?.metadata.languages?.[0]} translate={translateMany!}>
              <ChatPanel title={title} config={chatConfig} sheet={!wide} onClose={closeChat} onQuote={openQuote} ask={ask} reading={reading} recap={features.recap} book={bookFeatures} talkWith={features.talkWith} reached={reached} quiz={features.quiz} aroundMe={features.aroundMe} cover={bookInfo?.cover} />
            </ContentLanguageProvider>
          </aside>
        )}
      </div>

      {!wide && chatOpen && <div class="reader-scrim" onClick={closeChat} />}

      {showMobileChrome && (
        <>
          <div class="reader-scrim reader-scrim--light" onClick={hideChromeFromScrim} />
          <MobileChrome
            title={title}
            section={section}
            backUrl={params.backUrl}
            bookmarked={!!current}
            bookmarksEnabled={bookmarksEnabled}
            searchEnabled={searchEnabled}
            notesEnabled={annotationsEnabled}
            chatAvailable={chatAvailable}
            onToggleBookmark={toggleBookmark}
            readAloud={readAloudControl}
            onChat={() => {
              setChromeVisible(false);
              setSheetChatOpen(true);
            }}
            onPanel={(name) => {
              setChromeVisible(false);
              openPanel(name, null);
            }}
          />
          <div class="msb-mobile-footer">{footer}</div>
        </>
      )}

      {elsewhere && status.kind === "ready" && (
        <ElsewhereDialog
          there={Math.round((elsewhere.locations?.totalProgression ?? 0) * 100)}
          here={Math.round((locator?.locations?.totalProgression ?? 0) * 100)}
          onGo={() => {
            void reader.current?.goTo(elsewhere);
            setElsewhere(undefined);
          }}
          onStay={() => setElsewhere(undefined)}
        />
      )}

      {panel && (
        <Popover
          title={t(panel.name === "bookmarks" && annotationsEnabled ? "notes" : panel.name)}
          anchor={wide ? panel.anchor : null}
          wide={wide}
          onClose={closePanel}
          tall={panel.name === "search"}
          actions={
            panel.name === "preferences" ? (
              <PreferencesMenu onReset={() => changePrefs({ ...defaultReadingPreferences })} />
            ) : undefined
          }
        >
          {panel.name === "preferences" && (
            <PreferencesPanel
              preferences={prefs}
              showAccessibility={features.accessibilityPreferences}
              fixedLayout={fixedLayout}
              onChange={changePrefs}
            />
          )}
          {panel.name === "book" && (
            <BookSheet
              title={title}
              cover={bookInfo?.cover}
              details={bookInfo ? bookDetails(bookInfo.metadata, locale, params.productId) : []}
              description={plainDescription(bookInfo?.metadata.description)}
            />
          )}
          {panel.name === "contents" && (
            <ContentsPanel
              toc={toc}
              currentHref={locator?.href}
              isAvailable={!isFull && chapters.hrefs.length > 0 ? availableIn(chapters.hrefs) : undefined}
              onSelect={(href) => {
                closePanel();
                jump((r) => r.goToHref(href));
              }}
            />
          )}
          {panel.name === "search" && bookSearch && (
            <SearchPanel
              search={bookSearch}
              initialQuery={lastQuery.current}
              onQuery={(query) => (lastQuery.current = query)}
              onSelect={(hits, index, searching) => {
                closePanel();
                rememberCurrent();
                trailSearch.current?.abort();
                setTrail(null);
                showOccurrence(hits, index);
                if (searching) searchOnForTrail(lastQuery.current.trim());
              }}
            />
          )}
          {panel.name === "bookmarks" && (
            <NotesPanel
              annotations={annotations}
              bookmarks={bookmarks}
              positionCount={positionCount}
              onSelectAnnotation={(a) => {
                closePanel();
                jump((r) => r.goTo(a.locator));
              }}
              onEditAnnotation={(a) => {
                closePanel();
                setEditing({ id: a.id });
              }}
              onDeleteAnnotation={(a) => deleteAnnotation(a.id)}
              onSelectBookmark={(b) => {
                closePanel();
                jump((r) => r.goTo(b.locator));
              }}
              onDeleteBookmark={deleteBookmark}
            />
          )}
        </Popover>
      )}

      {annotationsEnabled && selection && !editing && !panel && (
        <SelectionToolbar
          rect={selection.rect}
          onColor={(color) => addHighlight(color)}
          onNote={() => addHighlight("green", true)}
          onExplain={features.explain ? explainSelection : undefined}
          onTranslate={translateInBar && translateOnAiServer ? translateSelection : undefined}
        />
      )}

      {editing && editedAnnotation && (
        <AnnotationCard
          key={editing.id}
          annotation={editedAnnotation}
          rect={editing.rect}
          sheet={!wide}
          onColor={(color) => changeAnnotation(editing.id, { color })}
          onNote={(note) => changeAnnotation(editing.id, { note })}
          onExplain={
            features.explain
              ? () => {
                  const rect = editing.rect;
                  setEditing(null);
                  explainPassage(editedAnnotation.locator.text?.highlight ?? "", rect);
                }
              : undefined
          }
          onDelete={() => deleteAnnotation(editing.id)}
          onClose={() => setEditing(null)}
        />
      )}

      {definition && (
        <DefinitionSheet
          key={definition.term}
          term={definition.term}
          rect={definition.rect}
          sheet={!wide}
          onChat={features.contextualChat && chatConfig ? () => askChat(definition.term) : undefined}
          onTranslate={() => setDefinition({ ...definition, translation: translationOf(definition.term) })}
          translation={definition.translation}
          translateOnServer={translateOnAiServer}
          onClose={() => setDefinition(null)}
        />
      )}

      {excerptFinished && <ExcerptEnd title={title} buyUrl={params.buyUrl} embedded={embedded} />}
    </div>
  );
}
